import * as React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Text, Textarea, Flex, cn, KEYS } from '@siteground/styleguide';
import { createSystemMessage } from '../../actions';
import { openFileUploadWindow, removeFileForUpload } from '../../actions/file-upload';
import {
  clientIsTyping,
  sendChatMessage,
  setChatStatus,
  uploadFiles
} from '../../app-communication/actions/from-client-frontend';
import {
  CHAT_STATE,
  FILE_UPLOAD_DISABLED_CODE,
  FILE_UPLOAD_DISABLED_INTL_KEY,
  SYSTEM_MESSAGE_TYPES
} from '../../constants';
import { DIALOGS } from '../../constants/sg-dialog';
import { isFileUploadEnabled } from '../../selectors/chat';
import { FileUploadChip } from '../file-upload-chip';
import { SGDialog, SGDialogCancel } from '../sg-dialog';
import './message-text-area.css';

const MAX_MESSAGE_LENGTH = 5000;
const TEXTAREA_DOM_ID = 'sg-chat-textarea';

function setTextAreaAutoSizeStyle(event) {
  const textarea = document.getElementById(TEXTAREA_DOM_ID);

  if (!textarea) {
    return;
  }

  // let text = event.target.value;
  // let lines = text.split(/\r|\r\n|\n/);
  // let count = lines.length;
  // console.log(count);

  if (event && event.key === 'Enter' && !event.shiftKey) {
    clearTextAreaAutoSizeStyle(event);
    return;
  }

  setTimeout(function () {
    textarea.style.cssText = `
      height: ${textarea.scrollHeight}px;
      max-height: 120px;
      overflow: auto;
    `;
  }, 0);
}

function clearTextAreaAutoSizeStyle(event) {
  const textarea = document.getElementById(TEXTAREA_DOM_ID);

  if (!textarea) {
    return;
  }

  if (event) {
    event.preventDefault();
  }

  textarea.removeAttribute('style');
}

const MessageTextArea = () => {
  const [message, setMessage] = React.useState('');
  const [hasLengthError, setHasLengthError] = React.useState(false);

  const enableFileUpload = useSelector(isFileUploadEnabled);

  const intl = useIntl();
  const dispatch = useDispatch();

  const sendMessage = (msg = '') => {
    const messageToSend = msg.trim();
    const canSendMessage = messageToSend.length && !hasLengthError;

    dispatch(
      uploadFiles({
        onComplete: ({ name, size }) => {
          // TODO need to talk about this one, using cluster message seems not okay, but copy paste the same logic also is not okay
          dispatch(
            createSystemMessage({
              intlKey: 'translate.chat.system.message.file.upload.success',
              type: SYSTEM_MESSAGE_TYPES.SUCCESS
            })
          );
        },
        onFailure: (error) => {
          if (error?.data?.code === FILE_UPLOAD_DISABLED_CODE) {
            dispatch(setChatStatus(CHAT_STATE.IN_PROGRESS));
            dispatch(
              createSystemMessage({
                intlKey: FILE_UPLOAD_DISABLED_INTL_KEY,
                type: SYSTEM_MESSAGE_TYPES.INSTRUCTION
              })
            );
          } else {
            dispatch(
              createSystemMessage({
                intlKey: 'translate.system.message.file.upload.error',
                type: SYSTEM_MESSAGE_TYPES.ERROR
              })
            );
          }
          dispatch(removeFileForUpload());
        }
      })
    );

    if (canSendMessage) {
      dispatch(sendChatMessage({ message: messageToSend }));

      setMessage('');
      setHasLengthError(false);
      clearTextAreaAutoSizeStyle();
    }
  };

  const textareaClasses = cn('message-box__textarea', enableFileUpload && 'message-box__textarea--file-upload-enabled');

  const actionButtonClasses = cn('message-box__action-button');

  const validationMessage = hasLengthError
    ? intl.formatMessage({ id: 'translate.chat.message.max.length.error' }, { length: MAX_MESSAGE_LENGTH })
    : undefined;

  const textAreaState = hasLengthError ? 'error' : undefined;

  return (
    <Flex
      style={{ width: '100%' }}
      padding={['medium', 'large']}
      align="center"
      justify="center"
      gap="x-small"
      wrap="nowrap"
      grow="1"
      className="message-box__wrapper"
    >
      <div className="message-box">
        <FileUploadChip />

        <div className="message-box__textarea-wrapper">
          <Textarea
            id={TEXTAREA_DOM_ID}
            className={textareaClasses}
            autoFocus
            rows={1}
            value={message}
            placeholder={intl.formatMessage({ id: 'translate.chat.textarea.placeholder' })}
            state={textAreaState}
            validationMessage={validationMessage}
            onChange={(event) => {
              const value = event.target.value || '';
              const hasError = value.length > MAX_MESSAGE_LENGTH;

              dispatch(clientIsTyping());

              setHasLengthError(hasError);
              setMessage(value);
            }}
            onKeyPress={(event) => {
              if (event.key === KEYS.ENTER && !event.shiftKey) {
                const message = event.target.value;
                sendMessage(message);
              }

              setTextAreaAutoSizeStyle(event);
            }}
          />

          <div className="message-box__action-buttons">
            {enableFileUpload && (
              <div className={actionButtonClasses} onClick={() => dispatch(openFileUploadWindow())}>
                <Icon color="light" name="editor-attachment" size="22" />
              </div>
            )}
          </div>
        </div>

        <SGDialog
          id={DIALOGS.FILE_UPLOAD_VALIDATION}
          icon="warning"
          state="inactive"
          footer={
            <SGDialogCancel
              id={DIALOGS.FILE_UPLOAD_VALIDATION}
              label={intl.formatMessage({ id: 'translate.generic.close' })}
            />
          }
        >
          <Text>{intl.formatMessage({ id: 'translate.chat.file.upload.size.limit.text' })}</Text>
        </SGDialog>
      </div>

      <div className={actionButtonClasses} onClick={() => sendMessage(message)}>
        <Icon color="primary" name="chat-send" size="24" />
      </div>
    </Flex>
  );
};

export default MessageTextArea;
